import { Route, Routes } from "react-router-dom";
import { lazy, Suspense } from "react";
import PrivateRoute from "./pages/authenticate/OidcPrivateRoute";
import AppLayout from "./web/layouts/AppLayout";

const Chat = lazy(() => import("./components/aiAgent/chat"));
const VerificationModal = lazy(() => import("./components/emailVerify"));
const MobileVerification = lazy(() => import("./components/mobileVerify"));
const NotFoundPage = lazy(() => import("./components/pageNotFound"));
const ForgotPassword = lazy(() => import("./components/passwordReset"));
const SetPassword = lazy(() => import("./components/passwordSet"));
const ResetPasswordRequest = lazy(
  () => import("./components/resetPasswordRequest"),
);
const UserAuthentication = lazy(
  () => import("./components/userAuthentication"),
);

const Dashboard = lazy(() => import("./pages/dashboard"));

const SignInPage = lazy(() =>
  import("./pages/signin/OidcSignIn").then((m) => ({ default: m.OidcSignIn })),
);
const Terms = lazy(() =>
  import("./pages/terms").then((m) => ({ default: m.Terms })),
);

// Web common pages
const OrganizationOverviewPage = lazy(
  () =>
    import("./web/commonPages/OrganizationOverview/OrganizationOverviewPage"),
);
const AlertSystemPage = lazy(
  () => import("./web/commonPages/alertSystem/page"),
);
const AlertListPage = lazy(() => import("./web/commonPages/alertList/table"));

// Web pages (tables)
const LabTestTable = lazy(() => import("./web/pages/LabTest/table"));
const CbgCascadeTable = lazy(() => import("./web/pages/CbgCascade/table"));
const FlowMeterTable = lazy(() => import("./web/pages/FlowMeter/table"));
const PowerMeterTable = lazy(() => import("./web/pages/PowerMeter/table"));
const CoverProtectionTable = lazy(
  () => import("./web/pages/CoverProtection/table"),
);
const DailyCheckTable = lazy(() => import("./web/pages/DailyCheck/table"));
const GasCompositionTable = lazy(
  () => import("./web/pages/GasComposition/table"),
);
const SludgeWastageTable = lazy(
  () => import("./web/pages/SludgeWastage/table"),
);
const SludgeCakeTrackerTable = lazy(
  () => import("./web/pages/SludgeCakeTracker/table"),
);
const EquipmentCurrentTable = lazy(
  () => import("./web/pages/EquipmentCurrent/table"),
);
const EquipmentVibrationTable = lazy(
  () => import("./web/pages/EquipmentVibration/table"),
);
const EquipmentVoltageTable = lazy(
  () => import("./web/pages/EquipmentVoltage/table"),
);
const EquipmentTemperatureTable = lazy(
  () => import("./web/pages/EquipmentTemperature/table"),
);
const EquipmentNoiseTable = lazy(
  () => import("./web/pages/EquipmentNoise/table"),
);
const EquipmentDowntimeTable = lazy(
  () => import("./web/pages/EquipmentDowntime/table"),
);
const EquipmentRunTimeTable = lazy(
  () => import("./web/pages/EquipmentRunTime/table"),
);
const EquipmentRuntimeMeterReadingTable = lazy(
  () => import("./web/pages/EquipmentRuntimeMeterReading/table"),
);
const FarmerSurveyTable = lazy(() => import("./web/pages/FarmerSurvey/table"));
const FertilizerSalesTrackingTable = lazy(
  () => import("./web/pages/FertilizerSalesTracking/table"),
);
const LiquidFertilizerOfftakeTable = lazy(
  () => import("./web/pages/LiquidFertilizerOfftake/table"),
);
const TankerTable = lazy(() => import("./web/pages/Tanker/table"));
const MbrCleaningLogTable = lazy(
  () => import("./web/pages/MbrCleaningLog/table"),
);
const PowerOutageTable = lazy(() => import("./web/pages/PowerOutage/table"));
const PumpDischargePressureTable = lazy(
  () => import("./web/pages/PumpDischargePressure/table"),
);
const RetailOutletMeterTable = lazy(
  () => import("./web/pages/RetailOutletMeter/table"),
);
const FeedstockTable = lazy(() => import("./web/pages/Feedstock/table"));
const WeighmentTable = lazy(() => import("./web/pages/Weighment/table"));
const ChemicalStockTrackerTable = lazy(() => import("./web/pages/ChemicalStockTracker/table"));
const DocumentRepository = lazy(() => import("./components/documents/DocumentRepository"));

// Web master pages (tables)
const ConversionFactorTable = lazy(() => import("./web/masterPages/ConversionFactorMaster/table"));
const CoverProtectionCoverMasterTable = lazy(() => import("./web/masterPages/CoverProtectionCoverMaster/table"));
const CropTypeMasterTable = lazy(() => import("./web/masterPages/CropTypeMaster/table"));
const DailyCheckBiomassColorMasterTable = lazy(() => import("./web/masterPages/DailyCheckBiomassColorMaster/table"));
const EquipmentCurrentEquipmentTable = lazy(() => import("./web/masterPages/EquipmentCurrentEquipmentMaster/table"));
const EquipmentCurrentMeasuringLocationTable = lazy(() => import("./web/masterPages/EquipmentCurrentMeasuringLocationMaster/table"));
const EquipmentIssuesMasterTable = lazy(() => import("./web/masterPages/EquipmentIssuesMaster/table"));
const EquipmentNoiseEquipmentTable = lazy(() => import("./web/masterPages/EquipmentNoiseEquipmentMaster/table"));
const EquipmentNoiseMeasuringLocationTable = lazy(() => import("./web/masterPages/EquipmentNoiseMeasuringLocationMaster/table"));
const EquipmentRuntimeEquipmentTagsTable = lazy(() => import("./web/masterPages/EquipmentRuntimeEquipmentTagsMaster/table"));
const EquipmentRuntimeMeterResetReasonTable = lazy(() => import("./web/masterPages/EquipmentRuntimeMeterResetReasonMaster/table"));
const EquipmentTemperatureEquipmentTable = lazy(() => import("./web/masterPages/EquipmentTemperatureEquipmentMaster/table"));
const EquipmentTemperatureMeasuringLocationTable = lazy(() => import("./web/masterPages/EquipmentTemperatureMeasuringLocationMaster/table"));
const EquipmentVibrationEquipmentTable = lazy(() => import("./web/masterPages/EquipmentVibrationEquipmentMaster/table"));
const EquipmentVibrationMeasuringLocationTable = lazy(() => import("./web/masterPages/EquipmentVibrationMeasuringLocationMaster/table"));
const EquipmentVoltageEquipmentTable = lazy(() => import("./web/masterPages/EquipmentVoltageEquipmentMaster/table"));
const EquipmentVoltageMeasuringLocationTable = lazy(() => import("./web/masterPages/EquipmentVoltageMeasuringLocationMaster/table"));
const FeedstockEqTankMasterTable = lazy(() => import("./web/masterPages/FeedstockEqTankMaster/table"));
const FertilizerSalesTrackingApplicationTypeMasterTable = lazy(() => import("./web/masterPages/FertilizerSalesTrackingApplicationTypeMaster/table"));
const FertilizerSalesTrackingCropTypeMasterTable = lazy(() => import("./web/masterPages/FertilizerSalesTrackingCropTypeMaster/table"));
const FertilizerSalesTrackingRetailerMasterTable = lazy(() => import("./web/masterPages/FertilizerSalesTrackingRetailerMaster/table"));
const FertilizerSalesTrackingSalesRepMasterTable = lazy(() => import("./web/masterPages/FertilizerSalesTrackingSalesRepMaster/table"));
const FertilizerSalesTrackingTankerMasterTable = lazy(() => import("./web/masterPages/FertilizerSalesTrackingTankerMaster/table"));
const FlowMeterResetReasonTable = lazy(() => import("./web/masterPages/FlowMeterResetReasonMaster/table"));
const GasCompositionLocationTable = lazy(() => import("./web/masterPages/GasCompositionLocationMaster/table"));
const LiquidFertilizerOfftakeMasterTable = lazy(() => import("./web/masterPages/LiquidFertilizerOfftakeMaster/table"));
const MaintenanceEquipmentMasterTable = lazy(() => import("./web/masterPages/MaintenanceEquipmentMaster/table"));
const MbrCleaningCleaningTypeMasterTable = lazy(() => import("./web/masterPages/MbrCleaningCleaningTypeMaster/table"));
const MbrCleaningMbrTrainMasterTable = lazy(() => import("./web/masterPages/MbrCleaningMbrTrainMaster/table"));
const PowerMeterResetReasonTable = lazy(() => import("./web/masterPages/PowerMeterResetReasonMaster/table"));
const PumpDischargePressurePumpTagsTable = lazy(() => import("./web/masterPages/PumpDischargePressurePumpTagsMaster/table"));
const RetailOutletMeterRetailOutletMasterTable = lazy(() => import("./web/masterPages/RetailOutletMeterRetailOutletMaster/table"));
const SludgeWastageValveTagsTable = lazy(() => import("./web/masterPages/SludgeWastageValveTagsMaster/table"));
const TankerMeerutApplicationTypeMasterTable = lazy(() => import("./web/masterPages/TankerMeerutApplicationTypeMaster/table"));
const UnloadingTankerMasterTable = lazy(() => import("./web/masterPages/UnloadingTankerMaster/table"));
const LoadingTankerMasterTable = lazy(() => import("./web/masterPages/LoadingTankerMaster/table"));
const CBGCascadesCascadeNumberMasterTable = lazy(() => import("./web/masterPages/CBGCascadesCascadeNumberMaster/table"));
const CBGCascadesRetailOutletMasterTable = lazy(() => import("./web/masterPages/CBGCascadesRetailOutletMaster/table"));
const CBGCascadesVehicleNumberMasterTable = lazy(() => import("./web/masterPages/CBGCascadesVehicleNumberMaster/table"));
const LabTestLocationMasterTable = lazy(() => import("./web/masterPages/LabTestLocationMaster/table"));
const LabTestSampleTypeMasterTable = lazy(() => import("./web/masterPages/LabTestSampleTypeMaster/table"));
const LabTestSampleStorageMasterTable = lazy(() => import("./web/masterPages/LabTestSampleStorageMaster/table"));
const LabTestTypeMasterTable = lazy(() => import("./web/masterPages/LabTestTypeMaster/table"));
const ChemicalMasterDataTable = lazy(() => import("./web/masterPages/ChemicalMasterData/table"));


function AppRoutes() {
  return (
    <Suspense fallback={null}>
      <Routes>
        {/* Public routes */}
        <Route exact path="/signin" element={<SignInPage />} />
        <Route exact path="/login" element={<SignInPage />} />
        <Route exact path="/terms" element={<Terms />} />
        <Route exact path="/reset-password/:id" element={<ForgotPassword />} />
        <Route exact path="/set-password/:id" element={<SetPassword />} />
        <Route
          exact
          path="/reset-password"
          element={<ResetPasswordRequest />}
        />
        <Route
          exact
          path="/verify/:id"
          element={<VerificationModal show={true} />}
        />
        <Route
          exact
          path="/mobile-verify/:id"
          element={<MobileVerification />}
        />
        <Route
          exact
          path="/authentication-page/:id"
          element={<UserAuthentication />}
        />

        {/* Protected routes */}
        <Route element={<AppLayout />}>
          <Route
            exact
            path="/"
            element={
              <PrivateRoute>
                <Dashboard />
              </PrivateRoute>
            }
          />
        <Route
          exact
          path="/dashboard"
          element={
            <PrivateRoute>
              <Dashboard />
            </PrivateRoute>
          }
        />
        <Route
          exact
          path="/ai-assistant/:id"
          element={
            <PrivateRoute>
              <Chat />
            </PrivateRoute>
          }
        />
        <Route
          path="/organization/:id"
          element={
            <PrivateRoute>
              <OrganizationOverviewPage />
            </PrivateRoute>
          }
        />

      <Route
        path="/lab-test/:id"
        element={
          <PrivateRoute>
            <LabTestTable />
          </PrivateRoute>
        }
      />
      <Route
        path="/cbg-cascade/:id"
        element={
          <PrivateRoute>
            <CbgCascadeTable />
          </PrivateRoute>
        }
      />
      <Route
        path="/flow-meter/:id"
        element={
          <PrivateRoute>
            <FlowMeterTable />
          </PrivateRoute>
        }
      />
      <Route
        path="/power-meter/:id"
        element={
          <PrivateRoute>
            <PowerMeterTable />
          </PrivateRoute>
        }
      />
      <Route
        path="/cover-protection/:id"
        element={
          <PrivateRoute>
            <CoverProtectionTable />
          </PrivateRoute>
        }
      />
      <Route
        path="/sludge-wastage/:id"
        element={
          <PrivateRoute>
            <SludgeWastageTable />
          </PrivateRoute>
        }
      />
      <Route
        path="/sludge-cake/:id"
        element={
          <PrivateRoute>
            <SludgeCakeTrackerTable />
          </PrivateRoute>
        }
      />
      <Route
        path="/gas-composition/:id"
        element={
          <PrivateRoute>
            <GasCompositionTable />
          </PrivateRoute>
        }
      />
      <Route
        path="/daily-check/:id"
        element={
          <PrivateRoute>
            <DailyCheckTable />
          </PrivateRoute>
        }
      />
      <Route
        path="/equipment-current/:id"
        element={
          <PrivateRoute>
            <EquipmentCurrentTable />
          </PrivateRoute>
        }
      />
      <Route
        path="/equipment-vibration/:id"
        element={
          <PrivateRoute>
            <EquipmentVibrationTable />
          </PrivateRoute>
        }
      />
      <Route
        path="/equipment-voltage/:id"
        element={
          <PrivateRoute>
            <EquipmentVoltageTable />
          </PrivateRoute>
        }
      />
      <Route
        path="/tanker/:id"
        element={
          <PrivateRoute>
            <TankerTable />
          </PrivateRoute>
        }
      />
      <Route
        path="/equipment-temperature/:id"
        element={
          <PrivateRoute>
            <EquipmentTemperatureTable />
          </PrivateRoute>
        }
      />
      <Route
        path="/equipment-noise/:id"
        element={
          <PrivateRoute>
            <EquipmentNoiseTable />
          </PrivateRoute>
        }
      />
      <Route
        path="/equipment-downtime/:id"
        element={
          <PrivateRoute>
            <EquipmentDowntimeTable />
          </PrivateRoute>
        }
      />
      <Route
        path="/equipment-run-time/:id"
        element={
          <PrivateRoute>
            <EquipmentRunTimeTable />
          </PrivateRoute>
        }
      />
      <Route
        path="/equipment-runtime-meter-reading/:id"
        element={
          <PrivateRoute>
            <EquipmentRuntimeMeterReadingTable />
          </PrivateRoute>
        }
      />
      <Route
        path="/farmer-survey/:id"
        element={
          <PrivateRoute>
            <FarmerSurveyTable />
          </PrivateRoute>
        }
      />
      <Route
        path="/feedstock/:id"
        element={
          <PrivateRoute>
            <FeedstockTable />
          </PrivateRoute>
        }
      />
      <Route
        path="/weighment/:id"
        element={
          <PrivateRoute>
            <WeighmentTable />
          </PrivateRoute>
        }
      />
      <Route
        path="/chemical-stock-tracker/:id"
        element={
          <PrivateRoute>
            <ChemicalStockTrackerTable />
          </PrivateRoute>
        }
      />
      <Route
        path="/fertilizer-sales-tracking/:id"
        element={
          <PrivateRoute>
            <FertilizerSalesTrackingTable />
          </PrivateRoute>
        }
      />
      <Route
        path="/mbr-cleaning-log/:id"
        element={
          <PrivateRoute>
            <MbrCleaningLogTable />
          </PrivateRoute>
        }
      />
      <Route
        path="/liquid-fertilizer-offtake/:id"
        element={
          <PrivateRoute>
            <LiquidFertilizerOfftakeTable />
          </PrivateRoute>
        }
      />
      <Route
        path="/pump-discharge-pressure/:id"
        element={
          <PrivateRoute>
            <PumpDischargePressureTable />
          </PrivateRoute>
        }
      />
      <Route
        path="/retail-outlet-meter/:id"
        element={
          <PrivateRoute>
            <RetailOutletMeterTable />
          </PrivateRoute>
        }
      />
      <Route
        path="/alerts/:id"
        element={
          <PrivateRoute>
            <AlertListPage />
          </PrivateRoute>
        }
      />
      <Route
        path="/power-outage/:id"
        element={
          <PrivateRoute>
            <PowerOutageTable />
          </PrivateRoute>
        }
      />
      <Route
        path="/documents/:id"
        element={
          <PrivateRoute>
            <DocumentRepository />
          </PrivateRoute>
        }
      />

        <Route
          path="/master/conversion-factor/:id"
          element={
            <PrivateRoute>
              <ConversionFactorTable />
            </PrivateRoute>
          }
        />
        <Route
          path="/master/cover-protection-cover/:id"
          element={
            <PrivateRoute>
              <CoverProtectionCoverMasterTable />
            </PrivateRoute>
          }
        />
        <Route
          path="/master/crop-type/:id"
          element={
            <PrivateRoute>
              <CropTypeMasterTable />
            </PrivateRoute>
          }
        />
        <Route
          path="/master/flow-meter-reset-reason/:id"
          element={
            <PrivateRoute>
              <FlowMeterResetReasonTable />
            </PrivateRoute>
          }
        />
        <Route
          path="/master/power-meter-reset-reason/:id"
          element={
            <PrivateRoute>
              <PowerMeterResetReasonTable />
            </PrivateRoute>
          }
        />
        <Route
          path="/master/gas-composition-location/:id"
          element={
            <PrivateRoute>
              <GasCompositionLocationTable />
            </PrivateRoute>
          }
        />
        <Route
          path="/master/sludge-wastage-valve-tags/:id"
          element={
            <PrivateRoute>
              <SludgeWastageValveTagsTable />
            </PrivateRoute>
          }
        />
        <Route
          path="/master/daily-check-biomass-color/:id"
          element={
            <PrivateRoute>
              <DailyCheckBiomassColorMasterTable />
            </PrivateRoute>
          }
        />
        <Route
          path="/master/equipment-current-equipment/:id"
          element={
            <PrivateRoute>
              <EquipmentCurrentEquipmentTable />
            </PrivateRoute>
          }
        />
        <Route
          path="/master/equipment-current-measuring-location/:id"
          element={
            <PrivateRoute>
              <EquipmentCurrentMeasuringLocationTable />
            </PrivateRoute>
          }
        />
        <Route
          path="/master/equipment-issues/:id"
          element={
            <PrivateRoute>
              <EquipmentIssuesMasterTable />
            </PrivateRoute>
          }
        />
        <Route
          path="/master/equipment-noise-equipment/:id"
          element={
            <PrivateRoute>
              <EquipmentNoiseEquipmentTable />
            </PrivateRoute>
          }
        />
        <Route
          path="/master/equipment-noise-measuring-location/:id"
          element={
            <PrivateRoute>
              <EquipmentNoiseMeasuringLocationTable />
            </PrivateRoute>
          }
        />
        <Route
          path="/master/equipment-runtime-equipment-tags/:id"
          element={
            <PrivateRoute>
              <EquipmentRuntimeEquipmentTagsTable />
            </PrivateRoute>
          }
        />
        <Route
          path="/master/equipment-runtime-meter-reset-reason/:id"
          element={
            <PrivateRoute>
              <EquipmentRuntimeMeterResetReasonTable />
            </PrivateRoute>
          }
        />
        <Route
          path="/master/equipment-temperature-equipment/:id"
          element={
            <PrivateRoute>
              <EquipmentTemperatureEquipmentTable />
            </PrivateRoute>
          }
        />
        <Route
          path="/master/equipment-temperature-measuring-location/:id"
          element={
            <PrivateRoute>
              <EquipmentTemperatureMeasuringLocationTable />
            </PrivateRoute>
          }
        />
        <Route
          path="/master/equipment-vibration-equipment/:id"
          element={
            <PrivateRoute>
              <EquipmentVibrationEquipmentTable />
            </PrivateRoute>
          }
        />
        <Route
          path="/master/equipment-vibration-measuring-location/:id"
          element={
            <PrivateRoute>
              <EquipmentVibrationMeasuringLocationTable />
            </PrivateRoute>
          }
        />
        <Route
          path="/master/equipment-voltage-equipment/:id"
          element={
            <PrivateRoute>
              <EquipmentVoltageEquipmentTable />
            </PrivateRoute>
          }
        />
        <Route
          path="/master/equipment-voltage-measuring-location/:id"
          element={
            <PrivateRoute>
              <EquipmentVoltageMeasuringLocationTable />
            </PrivateRoute>
          }
        />
        <Route
          path="/master/feedstock-eq-tank/:id"
          element={
            <PrivateRoute>
              <FeedstockEqTankMasterTable />
            </PrivateRoute>
          }
        />
        <Route
          path="/master/fertilizer-sales-tracking-application-type/:id"
          element={
            <PrivateRoute>
              <FertilizerSalesTrackingApplicationTypeMasterTable />
            </PrivateRoute>
          }
        />
        <Route
          path="/master/fertilizer-sales-tracking-crop-type/:id"
          element={
            <PrivateRoute>
              <FertilizerSalesTrackingCropTypeMasterTable />
            </PrivateRoute>
          }
        />
        <Route
          path="/master/fertilizer-sales-tracking-retailer/:id"
          element={
            <PrivateRoute>
              <FertilizerSalesTrackingRetailerMasterTable />
            </PrivateRoute>
          }
        />
        <Route
          path="/master/fertilizer-sales-tracking-sales-rep/:id"
          element={
            <PrivateRoute>
              <FertilizerSalesTrackingSalesRepMasterTable />
            </PrivateRoute>
          }
        />
        <Route
          path="/master/fertilizer-sales-tracking-tanker/:id"
          element={
            <PrivateRoute>
              <FertilizerSalesTrackingTankerMasterTable />
            </PrivateRoute>
          }
        />
        <Route
          path="/master/liquid-fertilizer-offtake-master/:id"
          element={
            <PrivateRoute>
              <LiquidFertilizerOfftakeMasterTable />
            </PrivateRoute>
          }
        />
        <Route
          path="/master/tanker-meerut-application-type/:id"
          element={
            <PrivateRoute>
              <TankerMeerutApplicationTypeMasterTable />
            </PrivateRoute>
          }
        />
        <Route
          path="/master/unloading-tanker-master/:id"
          element={
            <PrivateRoute>
              <UnloadingTankerMasterTable />
            </PrivateRoute>
          }
        />
        <Route
          path="/master/loading-tanker-master/:id"
          element={
            <PrivateRoute>
              <LoadingTankerMasterTable />
            </PrivateRoute>
          }
        />
        <Route
          path="/master/maintenance-equipment-master/:id"
          element={
            <PrivateRoute>
              <MaintenanceEquipmentMasterTable />
            </PrivateRoute>
          }
        />
        <Route
          path="/master/mbr-cleaning-mbr-train-master/:id"
          element={
            <PrivateRoute>
              <MbrCleaningMbrTrainMasterTable />
            </PrivateRoute>
          }
        />
        <Route
          path="/master/mbr-cleaning-cleaning-type-master/:id"
          element={
            <PrivateRoute>
              <MbrCleaningCleaningTypeMasterTable />
            </PrivateRoute>
          }
        />
        <Route
          path="/master/pump-discharge-pressure-pump-tags/:id"
          element={
            <PrivateRoute>
              <PumpDischargePressurePumpTagsTable />
            </PrivateRoute>
          }
        />
        <Route
          path="/master/retail-outlet-meter-retail-outlet-master/:id"
          element={
            <PrivateRoute>
              <RetailOutletMeterRetailOutletMasterTable />
            </PrivateRoute>
          }
        />

      <Route
        path="/master/cbg-cascades-cascade-number/:id"
        element={
          <PrivateRoute>
            <CBGCascadesCascadeNumberMasterTable />
          </PrivateRoute>
        }
      />
      <Route
        path="/master/cbg-cascades-retail-outlet/:id"
        element={
          <PrivateRoute>
            <CBGCascadesRetailOutletMasterTable />
          </PrivateRoute>
        }
      />
      <Route
        path="/master/cbg-cascades-vehicle-number/:id"
        element={
          <PrivateRoute>
            <CBGCascadesVehicleNumberMasterTable />
          </PrivateRoute>
        }
      />
      <Route
        path="/master/lab-test-location/:id"
        element={
          <PrivateRoute>
            <LabTestLocationMasterTable />
          </PrivateRoute>
        }
      />
      <Route
        path="/master/lab-test-sample-type/:id"
        element={
          <PrivateRoute>
            <LabTestSampleTypeMasterTable />
          </PrivateRoute>
        }
      />
      <Route
        path="/master/lab-test-sample-storage/:id"
        element={
          <PrivateRoute>
            <LabTestSampleStorageMasterTable />
          </PrivateRoute>
        }
      />
      <Route
        path="/master/lab-test-type/:id"
        element={
          <PrivateRoute>
            <LabTestTypeMasterTable />
          </PrivateRoute>
        }
      />
      <Route
        path="/master/chemical-master-data/:id"
        element={
          <PrivateRoute>
            <ChemicalMasterDataTable />
          </PrivateRoute>
        }
      />
        </Route>

        <Route path="*" element={<NotFoundPage />} />
      </Routes>
    </Suspense>
  );
}

export default AppRoutes;
